import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, ExternalLink, Sparkles, HelpCircle } from "lucide-react";
import BracketChart from "./BracketChart";
import SchoolSnapshot from "./SchoolSnapshot";
import { getBracketKeyFromIncome, assetAdjustment, formatRange, formatIncomeExact } from "@/lib/netprice-brackets";
import Spinner from "@/components/Spinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Result panel for a single school. The headline range comes from the
 * federal bracket math (College Scorecard cache) only — no hardcoded
 * threshold-based overrides. A separate "Live AI Net Price Estimator"
 * panel handles aid-policy reasoning and is rendered above this card.
 */
export default function NetPriceResult({ school, answers, onCompareAnother }) {
  const bracket = getBracketKeyFromIncome(answers.income);
  const baseNet = school.netPriceByIncome?.[bracket.apiKey];
  const adj = assetAdjustment(answers.homeEquity, answers.savings);

  // OOS uplift, applied only for public schools when the user picked
  // Out-of-State residency. Private schools charge the same tuition
  // regardless of residency, so there is no delta to add.
  const isPublic = school.ownership === 1 || school.type === "Public";
  const residency = answers.residency || "in_state";
  const oosUplift =
    isPublic && residency === "out_of_state"
      ? Math.max(0, (school.tuitionOutOfState || 0) - (school.tuitionInState || 0))
      : 0;

  const sticker = (school.costOfAttendance || school.tuitionOutOfState || school.tuitionInState || 0) + oosUplift;
  const incomeExact = formatIncomeExact(answers.income);

  // Federal-bracket math from College Scorecard cache. Add the OOS
  // uplift to both ends of the range when applicable.
  const federalRange = baseNet != null
    ? (() => {
        const r = formatRange(baseNet, adj);
        if (!oosUplift) return r;
        return { lo: r.lo + oosUplift, hi: r.hi + oosUplift, display: r.display };
      })()
    : null;

  const range = federalRange;
  const confidence = range ? "medium" : "low";

  const fourYr = range ? Math.round(((range.lo + range.hi) / 2) * 4) : 0;

  // Follow-up adjustments. The AI returns one question; the user can
  // answer "yes" and we adjust the headline by -15% (or +0% on "no").
  const [adjPercent, setAdjPercent] = useState(0); // -15, 0, or +10

  // AI-enhanced contextual paragraph.
  const [ai, setAi] = useState(null); // { explanation, caveat, follow_up, follow_up_impact }
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setAdjPercent(0);
    setAi(null);
    if (!range) return;

    setAiLoading(true);
    fetch(`${API}/netprice/ai-enhance`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        school_name: school.name,
        income: Number(answers.income) || 0,
        estimate_lo: range.lo,
        estimate_hi: range.hi,
        sticker_price: sticker,
        has_high_aid_policy: false,
        policy_note: null,
        confidence,
        residency,
      }),
    })
      .then(r => r.json())
      .then(j => { if (!cancel) setAi(j); })
      .catch(() => {})
      .finally(() => { if (!cancel) setAiLoading(false); });

    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school.id]);

  const adjusted = useMemo(() => {
    if (!range) return null;
    if (!adjPercent) return range;
    const factor = 1 + adjPercent / 100;
    const lo = Math.max(0, Math.round(range.lo * factor));
    const hi = Math.max(0, Math.round(range.hi * factor));
    return { lo, hi, display: lo === hi ? `$${lo.toLocaleString()}` : `$${lo.toLocaleString()} to $${hi.toLocaleString()}` };
  }, [range, adjPercent]);

  return (
    <div className="space-y-7" data-testid="netprice-result">
      {/* School header */}
      <div>
        <h1 className="font-heading leading-[1.05] text-gray-900"
          style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
          {school.name}
        </h1>
        <p className="text-sm mt-2 text-gray-600">
          {[school.city, school.state, school.type, school.size ? `${school.size.toLocaleString()} students` : null]
            .filter(Boolean).join(" · ")}
        </p>
        {answers.residency && (
          <div className="inline-flex items-center gap-2 mt-3 px-3 h-7 rounded-full text-xs"
            style={{ background: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe" }}
            data-testid="netprice-residency-tag">
            {answers.residency === "in_state" ? "In-State" : "Out-of-State"}
            {oosUplift > 0 && (
              <span className="text-[10px] uppercase tracking-widest" style={{ color: "#1d4ed8" }}>
                + ${oosUplift.toLocaleString()} OOS uplift
              </span>
            )}
          </div>
        )}
        {school.url && (
          <a href={`https://${school.url.replace(/^https?:\/\//, "")}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs mt-2 hover:underline"
            style={{ color: "#1d4ed8" }}
            data-testid="netprice-school-website">
            School website <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Headline */}
      <div className="rounded-xl p-7 sm:p-8"
        style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}
        data-testid="netprice-headline">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#1e40af" }}>
            Your estimated net price
          </div>
          <ConfidenceBadge level={confidence} />
        </div>

        <div className="font-heading mt-2 leading-none text-gray-900"
          style={{ fontSize: "clamp(2.4rem, 5vw, 3.6rem)" }}
          data-testid="netprice-amount">
          {adjusted ? adjusted.display : "Not enough data"}
        </div>
        <div className="mt-3 text-sm" style={{ color: "#1e40af" }}>
          per year, after grants and scholarships
        </div>
        <div className="mt-3 text-xs" style={{ color: "#1e40af", opacity: 0.85 }}>
          For a family earning {incomeExact} per year{adj > 0 && " (range reflects your asset profile)"}
        </div>
        {adjusted && sticker > 0 && (
          <div className="mt-5 grid grid-cols-3 gap-3 text-xs" style={{ color: "#1e40af" }}>
            <div><div style={{ opacity: 0.7 }}>Sticker price</div><div className="font-medium text-base mt-0.5 text-gray-900">${sticker.toLocaleString()}</div></div>
            <div><div style={{ opacity: 0.7 }}>You may save</div><div className="font-medium text-base mt-0.5" style={{ color: "#15803d" }}>${Math.max(0, sticker - adjusted.lo).toLocaleString()}/yr</div></div>
            <div><div style={{ opacity: 0.7 }}>4-year estimate</div><div className="font-medium text-base mt-0.5 text-gray-900">${fourYr.toLocaleString()}</div></div>
          </div>
        )}
      </div>

      {/* AI-enhanced reading */}
      <div className="rounded-xl p-5 sm:p-6 bg-white"
        style={{ border: "1px solid #bfdbfe" }}
        data-testid="netprice-ai-reading">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-3.5 h-3.5" style={{ color: "#1d4ed8" }} />
          <span className="text-[11px] uppercase tracking-[0.18em] font-medium text-gray-700">AidWaive AI reading</span>
        </div>
        {aiLoading ? (
          <Spinner variant="block" label="Reasoning over your income, this school's aid policy, and the Scorecard data." testId="netprice-ai-spinner" />
        ) : ai && ai.explanation ? (
          <>
            <p className="text-[15px] leading-relaxed text-gray-900">{ai.explanation}</p>
            {ai.caveat && (
              <p className="text-sm leading-relaxed mt-3 text-gray-600 italic">
                {ai.caveat}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">AI reading unavailable right now. The data below still applies.</p>
        )}
      </div>

      {/* Follow-up question pill */}
      {ai && ai.follow_up && (
        <div className="rounded-xl p-5 sm:p-6"
          style={{ background: "#fffbeb", border: "1px solid #fde68a" }}
          data-testid="netprice-followup">
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle className="w-3.5 h-3.5" style={{ color: "#92400e" }} />
            <span className="text-[11px] uppercase tracking-[0.18em] font-medium" style={{ color: "#92400e" }}>
              One quick question
            </span>
          </div>
          <p className="text-[15px] leading-relaxed text-gray-900">{ai.follow_up}</p>
          {ai.follow_up_impact && (
            <p className="text-xs mt-2" style={{ color: "#92400e" }}>{ai.follow_up_impact}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setAdjPercent(-15)}
              className="px-4 h-9 rounded-md text-xs font-medium transition-opacity hover:opacity-90"
              style={{
                background: adjPercent === -15 ? "#15803d" : "#ffffff",
                color: adjPercent === -15 ? "#ffffff" : "#15803d",
                border: "1px solid #15803d",
              }}
              data-testid="netprice-followup-yes">
              Yes — recalculate
            </button>
            <button
              type="button"
              onClick={() => setAdjPercent(0)}
              className="px-4 h-9 rounded-md text-xs font-medium transition-opacity hover:opacity-90"
              style={{
                background: adjPercent === 0 ? "#1f2937" : "#ffffff",
                color: adjPercent === 0 ? "#ffffff" : "#374151",
                border: "1px solid #d1d5db",
              }}
              data-testid="netprice-followup-no">
              No
            </button>
          </div>
        </div>
      )}

      {/* What's included */}
      <div className="rounded-xl p-5 sm:p-6 bg-white" style={{ border: "1px solid #bfdbfe" }}>
        <div className="text-[11px] uppercase tracking-[0.18em] mb-3 font-medium text-gray-500">
          What this number includes
        </div>
        <ul className="space-y-2 text-sm text-gray-900">
          <li><span style={{ color: "#15803d" }}>Includes</span> federal Pell Grant if you qualify</li>
          <li><span style={{ color: "#15803d" }}>Includes</span> institutional grants from the school</li>
          <li><span style={{ color: "#b91c1c" }}>Excludes</span> federal loans (those are repaid)</li>
          <li><span style={{ color: "#b91c1c" }}>Excludes</span> work study (you have to actually work for it)</li>
        </ul>
      </div>

      {/* Bracket chart */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] mb-4 font-medium text-gray-500">
          Net price by income level (all brackets)
        </div>
        <BracketChart brackets={school.netPriceByIncome} selectedKey={bracket.apiKey} />
        <p className="text-[11px] mt-4 text-gray-500">
          Source: {school.source}. Federal data updated annually.
        </p>
      </div>

      {/* Snapshot */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] mb-3 font-medium text-gray-500">
          School snapshot
        </div>
        <SchoolSnapshot school={school} />
      </div>

      {/* Caveat */}
      <div className="rounded-xl p-5 flex gap-3"
        style={{ background: "#fef3c7", border: "1px solid #fcd34d" }}
        data-testid="netprice-caveat">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#92400e" }} />
        <div>
          <div className="font-medium text-sm" style={{ color: "#92400e" }}>An estimate, not a quote.</div>
          <p className="text-sm mt-1" style={{ color: "#92400e" }}>
            This is based on what similar families actually paid, reported to the federal government.
            Your real cost depends on your specific FAFSA results, residency, merit aid, and this school's
            current aid policies. Use it as a starting point.
          </p>
        </div>
      </div>

      {/* Next steps */}
      <div className="rounded-xl p-6 bg-white" style={{ border: "1px solid #bfdbfe" }} data-testid="netprice-next">
        <div className="text-[11px] uppercase tracking-[0.18em] mb-4 font-medium text-gray-500">Next steps</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <NextLink to="/estimator" label="Estimate my SAI" testid="netprice-next-estimator" />
          <NextLink to="/compare"   label="Compare schools" testid="netprice-next-compare" />
          <NextLink to="/decoder"   label="Decode an award letter" testid="netprice-next-decoder" />
          <NextLink to="/chat"      label="Ask AidWaive AI" testid="netprice-next-chat" />
        </div>
      </div>

      <button
        onClick={onCompareAnother}
        className="inline-flex items-center gap-2 px-5 h-11 rounded-md text-sm font-medium hover:opacity-90"
        style={{ background: "#1d4ed8", color: "#ffffff" }}
        data-testid="netprice-add-school">
        Compare another school <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function ConfidenceBadge({ level }) {
  const map = {
    high:   { bg: "#ecfdf5", border: "#6ee7b7", text: "#15803d", label: "High confidence" },
    medium: { bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af", label: "Medium confidence" },
    low:    { bg: "#fef3c7", border: "#fcd34d", text: "#92400e", label: "Low confidence" },
  };
  const s = map[level] || map.medium;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.16em] font-semibold"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text }}
      data-testid="netprice-confidence">
      {s.label}
    </span>
  );
}

function NextLink({ to, label, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="flex items-center justify-between px-4 py-3 rounded-md transition-colors hover:bg-gray-50"
      style={{ border: "1px solid #bfdbfe", color: "#111928" }}>
      <span className="text-sm font-medium">{label}</span>
      <ArrowRight className="w-4 h-4" style={{ color: "#1d4ed8" }} />
    </Link>
  );
}
